import {useCallback} from 'react';

import {useCommonEventHook} from 'src/hooks/use-common-event-hook';
import {getAccountPage} from 'src/openapi/admin/apis/AccountInfoApi';
import {getAccountSsoConnectionName, inheritSsoSettings} from 'src/openapi/admin/apis/AccountSingleSignOnApi';
import {updateAccount} from 'src/openapi/admin/apis/UpdateAccountApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import type {InheritSsoSettingsRequest} from 'src/openapi/admin/models/InheritSsoSettingsRequest';
import type {ErrorPayload} from 'src/typedef';

type Props = {
    accountId: number,
    onSuccess?: () => void,
    onFail?: (error: string) => void,
    onParentSaved?: () => Promise<void> | void,
};

type InheritRequest = InheritSsoSettingsRequest & {
    saveParent?: boolean,
    accountName?: string,
};

const failure = (message: string): {type: string, payload: ErrorPayload} => ({
    type: '',
    payload: {success: false, error: true, data: {message}},
});

export const useInheritSsoSettings = ({accountId, onSuccess, onFail, onParentSaved}: Props) => {
    const apiDispatch = useAppDispatch();

    const eventAction = useCallback(async ({accountId: parentId, saveParent, accountName}: InheritRequest) => {
        let parentSaved = false;
        const fail = (message: string) => failure(parentSaved ? `Parent account was saved. ${message}` : message);

        try {
            const accountResponse = await apiDispatch(getAccountPage({accountId}));
            if (accountResponse.payload.error) {
                return fail(getError({error: accountResponse.payload.data, separator: ' '}));
            }
            const account = accountResponse.payload.data;
            if (parentId === accountId || (account.parentAccountId && account.parentAccountId !== parentId) ||
                (!account.parentAccountId && !saveParent)) {
                return fail('Parent account has changed. Refresh the page and try again.');
            }

            if (!account.parentAccountId && saveParent) {
                if (!account.name || !account.country || !account.entityTypeCode || !account.status ||
                    !account.templateUserId) {
                    return fail('Complete the required Account Information before setting a parent account.');
                }
                const saveResponse = await apiDispatch(updateAccount({
                    accountId,
                    accountUpdateRequest: {
                        accountName: account.name,
                        countryCode: account.country,
                        entityTypeCode: account.entityTypeCode,
                        statusCode: account.status,
                        templateUserId: account.templateUserId,
                        parentAccountId: parentId,
                    },
                }));
                if (saveResponse.payload.error) {
                    return fail(getError({error: saveResponse.payload.data, separator: ' '}));
                }
                parentSaved = true;
                await onParentSaved?.();
            }

            const nameResponse = await apiDispatch(getAccountSsoConnectionName({accountId: parentId}));
            if (nameResponse.payload.error) {
                return fail(getError({error: nameResponse.payload.data, separator: ' '}));
            }
            if (!nameResponse.payload.data.ssoConnectionName) {
                return fail(`Parent account ${accountName || parentId} has no SSO settings to inherit.`);
            }

            const response = await apiDispatch(inheritSsoSettings({
                accountId, inheritSsoSettingsRequest: {accountId: parentId},
            }));
            if (response.payload.error) {
                return fail(getError({error: response.payload.data, separator: ' '}));
            }
            return {type: '', payload: response.payload};
        } catch {
            return fail('Unable to inherit SSO settings. Please refresh the page and try again.');
        }
    }, [accountId, apiDispatch, onParentSaved]);

    return useCommonEventHook({eventAction, onSuccess, onFail});
};
